import { Component, Input, OnInit, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { ControlValueAccessor, FormControl } from '@ngneat/reactive-forms';
import { map } from 'rxjs/operators';

import { TruxDescription } from '@digital-platform/waste-profiles/domain';
import { WasteProfilesFacadeService } from '@digital-platform/waste-profiles/services';

@Component({
  selector: 'digital-platform-trux-material-description',
  templateUrl: './trux-material-description.component.html',
  styleUrls: ['./trux-material-description.component.scss'],
})
export class TruxMaterialDescriptionComponent
  implements OnInit, ControlValueAccessor
{
  truckOptions: TruxDescription[];
  control = new FormControl<TruxDescription>();
  public onTouched: () => void;

  @Input() public returnObject = false;

  private value: string | TruxDescription = null;

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private fascade: WasteProfilesFacadeService
  ) {
    if (controlDir !== null) {
      controlDir.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.fascade.getTruxMaterial()?.subscribe((data) => {
      this.truckOptions = data;
      if (this.value) {
        if (typeof this.value === 'string') {
          const description = this.findTruxDescription(this.value);
          this.control.setValue(description, { emitEvent: false });
        } else {
          this.control.setValue(this.value, { emitEvent: false });
        }
      }
    });
  }

  writeValue(value: string | TruxDescription): void {
    this.value = value;
    if (value) {
      if (typeof value === 'string') {
        const description = this.findTruxDescription(value);
        this.control.setValue(description, { emitEvent: false });
      } else {
        this.control.setValue(value, { emitEvent: false });
      }
    }
  }

  private findTruxDescription(value: string): TruxDescription {
    const found = this.truckOptions?.find(
      (option) => value === this.toTruxString(option)
    );

    if (found) {
      return found;
    }

    const [materialCode, materialDescription] = value.split('-', 2);

    const current: TruxDescription = {
      materialCode,
      materialDescription,
    };

    this.truckOptions?.push(current);

    return current;
  }

  private toTruxString(value: TruxDescription): string {
    return `${value.materialCode}-${value.materialDescription}`;
  }

  registerOnChange(fn: (value: string | TruxDescription) => void): void {
    this.control.valueChanges
      .pipe(
        map((value) => {
          if (!this.returnObject) {
            return this.toTruxString(value);
          }

          this.value = value;

          return value;
        })
      )
      .subscribe(fn);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  compareValues(
    a: string | TruxDescription,
    b: string | TruxDescription
  ): boolean {
    const normA =
      typeof a === 'string'
        ? a
        : `${a?.materialCode}-${a?.materialDescription}`;
    const normB =
      typeof b === 'string'
        ? b
        : `${b?.materialCode}-${b?.materialDescription}`;

    return normA === normB;
  }
}
